/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { transformLabelToAnnotation } from 'src/core/utils/labelingUtils';
import { LanguageCard } from 'src/languageStudio/appShell/prebuiltDemos/components/LanguageCard';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { resultCardStyles, resultCardTitleStyles } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import { DetectedLanguagesDocuments } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { ModelType } from 'src/luis/types/ModelType';
import { Label } from 'src/modules/labeling/types/Label';
import { Labeler } from 'src/packages/react-text-annotator/lib/esm';

export type VisualizedKeyPhrasesResultProps = {
    document: string;
    keyPhrases: string[];
    isLabelerRTL: boolean;
    isLabelerWordTokenized: boolean;
    detectedLanguage?: DetectedLanguagesDocuments;
};

const messages = defineMessages({
    keyPhrasesTitle: {
        id: 'languageStudio.visualizedKeyPhrasesResult.keyPhrasesTitle',
        defaultMessage: 'Key phrases'
    },
    labelText: {
        id: 'languageStudio.visualizedKeyPhrasesResult.labelText',
        defaultMessage: 'Key phrase'
    }
});

const theme = getScopedTheme('DemoPage');

export const VisualizedKeyPhrasesResult = (props: VisualizedKeyPhrasesResultProps) => {
    const { document, keyPhrases, isLabelerRTL, isLabelerWordTokenized, detectedLanguage } = props;
    const { localize } = useLocalization();

    const resultTokens = keyPhrases.map((keyPhrase, keyPhraseIndex) => {
        const accLength = document.indexOf(keyPhrase);

        return {
            id: keyPhraseIndex,
            tokens: { startTokenIndex: accLength, endTokenIndex: accLength + keyPhrase.length - 1 }
        };
    });

    const getLabel = (keyPhraseIndex: number): Label => {
        const eTokens = resultTokens.find(t => t.id === keyPhraseIndex).tokens;

        return {
            depth: 1,
            renderingLevel: 1,
            renderedAs: 'line',
            end: eTokens.endTokenIndex,
            start: eTokens.startTokenIndex,
            intrinsicEnd: eTokens.endTokenIndex,
            intrinsicStart: eTokens.startTokenIndex,
            entity: { id: keyPhraseIndex, name: localize(messages.labelText), roles: [], typeId: ModelType.Simple }
        };
    };

    const keyPhrasesCard = (keyPhraseIndex: number = -1) => (
        <Stack data-automation-id="keyPhraseCardsContainer" tokens={{ childrenGap: 7 }} styles={resultCardStyles}>
            <LuisText fontWeight={500} styles={resultCardTitleStyles(theme.locationEntityColor)}>
                {localize(messages.keyPhrasesTitle)}
            </LuisText>
            {keyPhraseIndex >= 0 ? (
                <LuisText>
                    {keyPhrases.slice(0, keyPhraseIndex).join(', ')}
                    {keyPhraseIndex !== 0 && ','}
                    &nbsp;
                    <LuisText fontWeight={500}>
                        {keyPhrases[keyPhraseIndex]}
                        {keyPhraseIndex !== keyPhrases.length - 1 && ','}
                    </LuisText>
                    &nbsp;
                    {keyPhrases.slice(keyPhraseIndex + 1).join(', ')}
                </LuisText>
            ) : (
                <LuisText>{keyPhrases.join(', ')}</LuisText>
            )}
        </Stack>
    );

    const annotations = React.useMemo(
        () =>
            keyPhrases
                .map((_, kIndex) => getLabel(kIndex))
                .map(label =>
                    transformLabelToAnnotation(label, keyPhrasesCard(label.entity.id as number), () => theme.locationEntityColor)
                ),
        [keyPhrases]
    );

    return (
        <Stack styles={{ root: { width: '100%' } }} verticalAlign="end" tokens={{ childrenGap: 25 }}>
            {keyPhrasesCard()}

            <LanguageCard language={detectedLanguage?.documents[0].detectedLanguage} />

            <Stack tokens={{ childrenGap: 10 }}>
                <LuisText as="h3" fontWeight={500}>
                    {localize(commonDemosMessages.originalText)}
                </LuisText>
                <Labeler
                    text={document}
                    annotations={annotations}
                    labelerConfigs={{
                        isDisabled: true,
                        isRtl: isLabelerRTL,
                        tokenizationType: isLabelerWordTokenized ? 'word' : 'character'
                    }}
                />
            </Stack>
        </Stack>
    );
};
